import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./topImageLightbox.scss";
import type { ImageLightboxRect } from "../../contexts/imageLightbox.recoil";

interface ImageLightboxProps {
	src: string;
	alt: string;
	originRect: ImageLightboxRect;
	onClose: () => void;
}

interface Rect {
	top: number;
	left: number;
	width: number;
	height: number;
}

const transitionMs = 300;

const toRect = (rect: ImageLightboxRect): Rect => ({
	top: rect.top,
	left: rect.left,
	width: rect.width,
	height: rect.height,
});

const ImageLightbox: React.FC<ImageLightboxProps> = ({ src, alt, originRect, onClose }) => {
	const [currentRect, setCurrentRect] = useState<Rect>(() => toRect(originRect));
	const [targetRect, setTargetRect] = useState<Rect | null>(null);
	const [isVisible, setIsVisible] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const imageRef = useRef<HTMLImageElement>(null);
	const closedRef = useRef(false);

	const getAspectRatio = useCallback(() => {
		const img = imageRef.current;
		const naturalWidth = img?.naturalWidth ?? 0;
		const naturalHeight = img?.naturalHeight ?? 0;
		if (naturalWidth > 0 && naturalHeight > 0) {
			return naturalWidth / naturalHeight;
		}
		if (originRect.width > 0 && originRect.height > 0) {
			return originRect.width / originRect.height;
		}
		return 3 / 4;
	}, [originRect.height, originRect.width]);

	const computeTargetRect = useCallback((): Rect => {
		const aspectRatio = getAspectRatio();
		const maxHeight = window.innerHeight * 0.9;
		const maxWidth = window.innerWidth * 0.9;
		let width = Math.min(maxWidth, maxHeight * aspectRatio);
		let height = width / aspectRatio;

		if (height > maxHeight) {
			height = maxHeight;
			width = height * aspectRatio;
		}

		return {
			top: (window.innerHeight - height) / 2,
			left: (window.innerWidth - width) / 2,
			width,
			height,
		};
	}, [getAspectRatio]);

	const updateTargetRect = useCallback(() => {
		setTargetRect(computeTargetRect());
	}, [computeTargetRect]);

	const requestClose = useCallback(() => {
		if (isClosing) {
			return;
		}
		setIsClosing(true);
		setIsVisible(false);
		setCurrentRect(toRect(originRect));
	}, [isClosing, originRect]);

	const finalizeClose = useCallback(() => {
		if (closedRef.current) {
			return;
		}
		closedRef.current = true;
		onClose();
	}, [onClose]);

	useLayoutEffect(() => {
		setCurrentRect(toRect(originRect));
		setIsVisible(false);
	}, [originRect]);

	useEffect(() => {
		updateTargetRect();
		window.addEventListener("resize", updateTargetRect);
		return () => window.removeEventListener("resize", updateTargetRect);
	}, [updateTargetRect]);

	useEffect(() => {
		if (!targetRect) {
			return;
		}
		const animationId = window.requestAnimationFrame(() => {
			setCurrentRect(targetRect);
			setIsVisible(true);
		});
		return () => window.cancelAnimationFrame(animationId);
	}, [targetRect]);

	useEffect(() => {
		closedRef.current = false;
	}, [isClosing]);

	useEffect(() => {
		if (!isClosing) {
			return;
		}
		const timeoutId = window.setTimeout(() => {
			finalizeClose();
		}, transitionMs + 50);
		return () => window.clearTimeout(timeoutId);
	}, [finalizeClose, isClosing]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				requestClose();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [requestClose]);

	useEffect(() => {
		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = originalOverflow;
		};
	}, []);

	if (typeof document === "undefined") {
		return null;
	}

	return createPortal(
		<div
			className={`top-image-lightbox${isVisible ? " is-visible" : ""}${isClosing ? " is-closing" : ""}`}
			role="dialog"
			aria-modal="true"
			onClick={requestClose}
		>
			<button
				type="button"
				className="til-close"
				aria-label="Close image preview"
				onClick={(event) => {
					event.stopPropagation();
					requestClose();
				}}
			>
				<span aria-hidden="true">&times;</span>
			</button>
			<img
				ref={imageRef}
				src={src}
				alt={alt}
				className="til-image"
				style={{
					top: `${currentRect.top}px`,
					left: `${currentRect.left}px`,
					width: `${currentRect.width}px`,
					height: `${currentRect.height}px`,
				}}
				onClick={(event) => {
					event.stopPropagation();
					requestClose();
				}}
				onLoad={updateTargetRect}
				onTransitionEnd={(event) => {
					if (!isClosing || event.propertyName !== "width") {
						return;
					}
					finalizeClose();
				}}
			/>
		</div>,
		document.body
	);
};

export default ImageLightbox;
